import React, { useState, useEffect } from 'react';
import {
  Window,
  WindowHeader,
  WindowContent,
  Button,
  TextInput,
  Fieldset,
  GroupBox,
  Separator,
  Toolbar,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableHeadCell,
  TableDataCell,
  Select,
  Panel
} from 'react95';
import { useTransactions, useDebtAccounts, usePaymentArrangements } from '../hooks';
import { 
  createPaymentTransaction,
  createAdjustmentTransaction,
  createContactTransaction,
  createArrangementTransaction,
  TRANSACTION_TYPES,
  CONTACT_TYPES,
  sortTransactionsByDate,
  filterTransactionsByType,
  filterTransactionsByDateRange
} from '../models/TransactionHistory';
import { formatCurrency } from '../models/DebtAccount';
import { processPayment } from '../utils/financialCalculations';

/**
 * Transaction History Component
 * 
 * Features implemented according to task 7 requirements:
 * - Transaction history component with chronological display
 * - Automatic audit trail creation for all activities
 * - Transaction filtering and search
 * - Transaction detail views
 * 
 * Requirements: 5.1, 5.2, 5.3, 5.4, 5.5
 */
const TransactionHistory = ({ 
  clientId = null,
  onTransactionAdded = () => {},
  onPaymentProcessed = () => {},
  showAddTransaction = true 
}) => {
  const transactions = useTransactions();
  const debtAccounts = useDebtAccounts();
  const paymentArrangements = usePaymentArrangements();
  
  // Component state
  const [selectedView, setSelectedView] = useState('all'); // 'all', 'payments', 'adjustments', 'contacts', 'arrangements'
  const [searchTerm, setSearchTerm] = useState('');
  const [dateFilter, setDateFilter] = useState({
    startDate: '',
    endDate: ''
  });
  const [showAddModal, setShowAddModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  
  // Transaction form state
  const [transactionForm, setTransactionForm] = useState({
    type: TRANSACTION_TYPES.PAYMENT,
    amount: '',
    description: '',
    reference: '',
    contactType: CONTACT_TYPES.PHONE_CALL,
    paymentMethod: 'check',
    adjustmentReason: '',
    arrangementDetails: {}
  });
  
  // Filtered transactions
  const [filteredTransactions, setFilteredTransactions] = useState([]);
  const [clientTransactions, setClientTransactions] = useState([]);

  // Load and filter transactions when clientId or filters change
  useEffect(() => {
    if (clientId) {
      let allTransactions = transactions.getTransactionsByClientId(clientId);
      
      // Apply type filter
      if (selectedView !== 'all') {
        const typeMap = {
          'payments': TRANSACTION_TYPES.PAYMENT,
          'adjustments': TRANSACTION_TYPES.ADJUSTMENT,
          'contacts': TRANSACTION_TYPES.CONTACT,
          'arrangements': TRANSACTION_TYPES.ARRANGEMENT
        };
        allTransactions = filterTransactionsByType(allTransactions, typeMap[selectedView]);
      }
      
      // Apply date filter
      if (dateFilter.startDate && dateFilter.endDate) {
        const startDate = new Date(dateFilter.startDate);
        const endDate = new Date(dateFilter.endDate);
        endDate.setHours(23, 59, 59); // Include full end date
        allTransactions = filterTransactionsByDateRange(allTransactions, startDate, endDate);
      }
      
      // Apply search filter
      if (searchTerm.trim()) {
        const term = searchTerm.toLowerCase();
        allTransactions = allTransactions.filter(transaction => 
          transaction.description.toLowerCase().includes(term) ||
          (transaction.reference && transaction.reference.toLowerCase().includes(term)) ||
          transaction.type.toLowerCase().includes(term)
        );
      }
      
      // Sort by date (most recent first)
      const sortedTransactions = sortTransactionsByDate(allTransactions);
      
      setClientTransactions(transactions.getTransactionsByClientId(clientId));
      setFilteredTransactions(sortedTransactions);
    } else {
      setClientTransactions([]);
      setFilteredTransactions([]);
    }
  }, [clientId, transactions.items, selectedView, searchTerm, dateFilter]);

  // Handle view change
  const handleViewChange = (view) => {
    setSelectedView(view);
  };

  // Handle search
  const handleSearch = (term) => {
    setSearchTerm(term);
  };

  // Handle date filter
  const handleDateFilter = (field, value) => {
    setDateFilter(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Clear filters
  const clearFilters = () => {
    setSearchTerm('');
    setDateFilter({ startDate: '', endDate: '' });
    setSelectedView('all');
  };

  // Handle transaction form input
  const handleFormInput = (field, value) => {
    setTransactionForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Add new transaction with automatic audit trail
  const handleAddTransaction = async () => {
    if (!clientId) return;

    try {
      let newTransaction;
      const userId = 'current_user'; // In real app, get from auth context
      
      switch (transactionForm.type) {
        case TRANSACTION_TYPES.PAYMENT:
          // Create payment transaction
          newTransaction = createPaymentTransaction(
            clientId,
            parseFloat(transactionForm.amount) || 0,
            transactionForm.description || `Payment of ${formatCurrency(parseFloat(transactionForm.amount) || 0)}`,
            transactionForm.reference,
            userId
          );
          
          // Process payment on debt accounts
          const clientDebtAccounts = debtAccounts.getDebtAccountsByClientId(clientId);
          if (clientDebtAccounts.length > 0) {
            // Apply payment to first account (in real app, let user choose)
            const account = clientDebtAccounts[0];
            await debtAccounts.recordPaymentForAccount(
              account.id, 
              parseFloat(transactionForm.amount) || 0
            );
            onPaymentProcessed({
              accountId: account.id,
              amount: parseFloat(transactionForm.amount) || 0
            });
          }
          break;
          
        case TRANSACTION_TYPES.ADJUSTMENT:
          newTransaction = createAdjustmentTransaction(
            clientId,
            parseFloat(transactionForm.amount) || 0,
            transactionForm.description || `Account adjustment: ${transactionForm.adjustmentReason}`,
            userId
          );
          break;
          
        case TRANSACTION_TYPES.CONTACT:
          newTransaction = createContactTransaction(
            clientId,
            transactionForm.contactType,
            transactionForm.description || `${transactionForm.contactType} contact attempt`,
            userId
          );
          break;
          
        case TRANSACTION_TYPES.ARRANGEMENT:
          newTransaction = createArrangementTransaction(
            clientId,
            transactionForm.description || 'Payment arrangement created/modified',
            transactionForm.arrangementDetails,
            userId
          );
          break;
          
        default:
          throw new Error('Invalid transaction type');
      }
      
      // Save transaction
      const result = await transactions.create(newTransaction);
      
      if (result.success) {
        // Reset form
        setTransactionForm({
          type: TRANSACTION_TYPES.PAYMENT,
          amount: '',
          description: '',
          reference: '',
          contactType: CONTACT_TYPES.PHONE_CALL,
          paymentMethod: 'check',
          adjustmentReason: '',
          arrangementDetails: {}
        });
        
        setShowAddModal(false);
        onTransactionAdded(result.data);
      }
    } catch (error) {
      console.error('Error adding transaction:', error);
      alert('Error adding transaction: ' + error.message);
    }
  };

  // Show transaction details
  const handleShowDetails = (transaction) => {
    setSelectedTransaction(transaction);
    setShowDetailModal(true);
  };

  // Get transaction type color
  const getTransactionTypeColor = (type) => {
    switch (type) {
      case TRANSACTION_TYPES.PAYMENT:
        return '#006600';
      case TRANSACTION_TYPES.ADJUSTMENT:
        return '#0066cc';
      case TRANSACTION_TYPES.FEE:
      case TRANSACTION_TYPES.LEGAL_FEE:
      case TRANSACTION_TYPES.DEBT_FEE:
      case TRANSACTION_TYPES.INTEREST:
        return '#cc6600';
      case TRANSACTION_TYPES.CONTACT:
        return '#666666';
      case TRANSACTION_TYPES.ARRANGEMENT:
        return '#6600cc';
      default:
        return '#808080';
    }
  };

  // Get amount color
  const getAmountColor = (type, amount) => {
    if (!amount) return '#666';
    
    switch (type) {
      case TRANSACTION_TYPES.PAYMENT:
        return '#006600';
      case TRANSACTION_TYPES.ADJUSTMENT:
        return amount > 0 ? '#cc0000' : '#006600';
      default:
        return '#cc0000';
    }
  };

  // Render transaction form based on type
  const renderTransactionForm = () => {
    return (
      <div style={{ padding: '15px' }}>
        <div style={{ marginBottom: '15px' }}>
          <label style={{ display: 'block', marginBottom: '5px', fontSize: '11px', fontWeight: 'bold' }}>
            Transaction Type:
          </label>
          <Select
            value={transactionForm.type}
            onChange={(value) => handleFormInput('type', value)}
            options={[
              { value: TRANSACTION_TYPES.PAYMENT, label: 'Payment' },
              { value: TRANSACTION_TYPES.ADJUSTMENT, label: 'Adjustment' },
              { value: TRANSACTION_TYPES.CONTACT, label: 'Contact' },
              { value: TRANSACTION_TYPES.ARRANGEMENT, label: 'Arrangement' }
            ]}
            style={{ width: '100%' }}
          />
        </div>

        {/* Amount field for payments and adjustments */}
        {(transactionForm.type === TRANSACTION_TYPES.PAYMENT || 
          transactionForm.type === TRANSACTION_TYPES.ADJUSTMENT) && (
          <div style={{ marginBottom: '15px' }}>
            <label style={{ display: 'block', marginBottom: '5px', fontSize: '11px', fontWeight: 'bold' }}>
              Amount:
            </label>
            <TextInput
              value={transactionForm.amount}
              onChange={(e) => handleFormInput('amount', e.target.value)}
              placeholder="0.00"
              style={{ width: '100%' }}
            />
          </div>
        )}

        {/* Contact type for contact transactions */}
        {transactionForm.type === TRANSACTION_TYPES.CONTACT && (
          <div style={{ marginBottom: '15px' }}>
            <label style={{ display: 'block', marginBottom: '5px', fontSize: '11px', fontWeight: 'bold' }}>
              Contact Type:
            </label>
            <Select
              value={transactionForm.contactType}
              onChange={(value) => handleFormInput('contactType', value)}
              options={[
                { value: CONTACT_TYPES.PHONE_CALL, label: 'Phone Call' },
                { value: CONTACT_TYPES.EMAIL, label: 'Email' },
                { value: CONTACT_TYPES.LETTER, label: 'Letter' },
                { value: CONTACT_TYPES.IN_PERSON, label: 'In Person' },
                { value: CONTACT_TYPES.SMS, label: 'SMS' }
              ]}
              style={{ width: '100%' }}
            />
          </div>
        )}

        {/* Reference field for payments */}
        {transactionForm.type === TRANSACTION_TYPES.PAYMENT && (
          <div style={{ marginBottom: '15px' }}>
            <label style={{ display: 'block', marginBottom: '5px', fontSize: '11px', fontWeight: 'bold' }}>
              Reference (Check #, etc.):
            </label>
            <TextInput
              value={transactionForm.reference}
              onChange={(e) => handleFormInput('reference', e.target.value)}
              placeholder="Check number, confirmation code, etc."
              style={{ width: '100%' }}
            />
          </div>
        )}

        {/* Description field */}
        <div style={{ marginBottom: '15px' }}>
          <label style={{ display: 'block', marginBottom: '5px', fontSize: '11px', fontWeight: 'bold' }}>
            Description:
          </label>
          <textarea
            value={transactionForm.description}
            onChange={(e) => handleFormInput('description', e.target.value)}
            placeholder="Enter transaction description..."
            style={{
              width: '100%',
              height: '80px',
              fontFamily: 'MS Sans Serif',
              fontSize: '11px',
              border: '2px inset #c0c0c0',
              padding: '4px',
              resize: 'vertical'
            }}
          />
        </div>

        <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
          <Button onClick={() => setShowAddModal(false)}>
            Cancel
          </Button>
          <Button 
            onClick={handleAddTransaction}
            disabled={!transactionForm.description.trim() || 
              (transactionForm.type === TRANSACTION_TYPES.PAYMENT && !transactionForm.amount)}
          >
            Add Transaction
          </Button>
        </div>
      </div>
    );
  };

  // Render transaction details modal
  const renderTransactionDetails = () => {
    if (!selectedTransaction) return null;

    return (
      <div style={{ padding: '15px' }}>
        <div style={{ marginBottom: '15px' }}>
          <h3 style={{ margin: '0 0 10px 0', fontSize: '14px' }}>Transaction Details</h3>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', fontSize: '11px' }}>
          <div>
            <div style={{ marginBottom: '10px' }}>
              <strong>Transaction ID:</strong><br/>
              {selectedTransaction.id}
            </div>
            <div style={{ marginBottom: '10px' }}>
              <strong>Type:</strong><br/>
              <span style={{
                padding: '2px 6px',
                backgroundColor: getTransactionTypeColor(selectedTransaction.type),
                color: 'white',
                borderRadius: '3px',
                fontSize: '10px'
              }}>
                {selectedTransaction.type.toUpperCase()}
              </span>
            </div>
            <div style={{ marginBottom: '10px' }}>
              <strong>Date & Time:</strong><br/>
              {new Date(selectedTransaction.timestamp).toLocaleString()}
            </div>
            {selectedTransaction.amount > 0 && (
              <div style={{ marginBottom: '10px' }}>
                <strong>Amount:</strong><br/>
                <span style={{ 
                  fontSize: '12px', 
                  fontWeight: 'bold',
                  color: getAmountColor(selectedTransaction.type, selectedTransaction.amount)
                }}>
                  {formatCurrency(selectedTransaction.amount)}
                </span>
              </div>
            )}
          </div>
          <div>
            <div style={{ marginBottom: '10px' }}>
              <strong>User:</strong><br/>
              {selectedTransaction.userId}
            </div>
            {selectedTransaction.reference && (
              <div style={{ marginBottom: '10px' }}>
                <strong>Reference:</strong><br/>
                {selectedTransaction.reference}
              </div>
            )}
            {selectedTransaction.contactType && (
              <div style={{ marginBottom: '10px' }}>
                <strong>Contact Type:</strong><br/>
                {selectedTransaction.contactType.replace('_', ' ').toUpperCase()}
              </div>
            )}
          </div>
        </div>

        <div style={{ marginTop: '15px' }}>
          <strong>Description:</strong><br/>
          <div style={{ 
            marginTop: '5px', 
            padding: '8px', 
            backgroundColor: '#f8f8f8',
            border: '1px solid #e0e0e0',
            fontSize: '11px'
          }}>
            {selectedTransaction.description}
          </div>
        </div>

        {selectedTransaction.metadata && Object.keys(selectedTransaction.metadata).length > 0 && (
          <div style={{ marginTop: '15px' }}>
            <strong>Additional Details:</strong><br/>
            <div style={{ 
              marginTop: '5px', 
              padding: '8px', 
              backgroundColor: '#f8f8f8',
              border: '1px solid #e0e0e0',
              fontSize: '10px'
            }}>
              <pre>{JSON.stringify(selectedTransaction.metadata, null, 2)}</pre>
            </div>
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '20px' }}>
          <Button onClick={() => setShowDetailModal(false)}>
            Close
          </Button>
        </div>
      </div>
    );
  };

  return (
    <Window style={{ width: '100%', minHeight: '600px' }}>
      <WindowHeader>
        <span>📋 Transaction History{clientId ? ` - Client ${clientId}` : ''}</span>
      </WindowHeader>
      
      {/* Toolbar */}
      <Toolbar style={{ justifyContent: 'flex-start' }}>
        <Button 
          size="sm" 
          onClick={() => handleViewChange('all')}
          style={{ 
            backgroundColor: selectedView === 'all' ? '#0066cc' : undefined,
            height: '32px',
            padding: '6px 12px',
            fontSize: '11px'
          }}
        >
          📋 All
        </Button>
        <Button 
          size="sm" 
          onClick={() => handleViewChange('payments')}
          style={{ 
            backgroundColor: selectedView === 'payments' ? '#0066cc' : undefined,
            height: '32px',
            padding: '6px 12px',
            fontSize: '11px'
          }}
        >
          💰 Payments
        </Button>
        <Button 
          size="sm" 
          onClick={() => handleViewChange('adjustments')}
          style={{ 
            backgroundColor: selectedView === 'adjustments' ? '#0066cc' : undefined,
            height: '32px',
            padding: '6px 12px',
            fontSize: '11px'
          }}
        >
          📊 Adjustments
        </Button>
        <Button 
          size="sm" 
          onClick={() => handleViewChange('contacts')}
          style={{ 
            backgroundColor: selectedView === 'contacts' ? '#0066cc' : undefined,
            height: '32px',
            padding: '6px 12px',
            fontSize: '11px'
          }}
        >
          📞 Contacts
        </Button>
        <Button 
          size="sm" 
          onClick={() => handleViewChange('arrangements')}
          style={{ 
            backgroundColor: selectedView === 'arrangements' ? '#0066cc' : undefined,
            height: '32px',
            padding: '6px 12px',
            fontSize: '11px'
          }}
        >
          📅 Arrangements
        </Button>
        <Separator orientation="vertical" />
        {showAddTransaction && clientId && (
          <>
            <Button 
              size="sm" 
              onClick={() => setShowAddModal(true)}
              style={{ 
                height: '32px',
                padding: '6px 12px',
                fontSize: '11px'
              }}
            >
              ➕ Add Transaction
            </Button>
            <Separator orientation="vertical" />
          </>
        )}
        <Button 
          size="sm" 
          onClick={clearFilters}
          style={{ 
            height: '32px',
            padding: '6px 12px',
            fontSize: '11px'
          }}
        >
          🗑️ Clear Filters
        </Button>
        <Separator orientation="vertical" />
        <Button 
          size="sm"
          style={{ 
            height: '32px',
            padding: '6px 12px',
            fontSize: '11px'
          }}
        >
          🖨️ Print
        </Button>
        <Button 
          size="sm"
          style={{ 
            height: '32px',
            padding: '6px 12px',
            fontSize: '11px'
          }}
        >
          📧 Export
        </Button>
      </Toolbar>

      <WindowContent>
        {clientId ? (
          <div>
            {/* Filters Section */}
            <Fieldset label="Filters & Search" style={{ marginBottom: '15px' }}>
              <div style={{ padding: '10px', display: 'flex', gap: '15px', alignItems: 'end' }}>
                <div style={{ flex: 1 }}>
                  <label style={{ display: 'block', marginBottom: '5px', fontSize: '11px', fontWeight: 'bold' }}>
                    Search:
                  </label>
                  <TextInput
                    value={searchTerm}
                    onChange={(e) => handleSearch(e.target.value)}
                    placeholder="Search description, reference, or type..."
                    style={{ width: '100%' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '5px', fontSize: '11px', fontWeight: 'bold' }}>
                    From Date:
                  </label>
                  <TextInput
                    type="date"
                    value={dateFilter.startDate}
                    onChange={(e) => handleDateFilter('startDate', e.target.value)}
                    style={{ width: '140px' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '5px', fontSize: '11px', fontWeight: 'bold' }}>
                    To Date:
                  </label>
                  <TextInput
                    type="date"
                    value={dateFilter.endDate}
                    onChange={(e) => handleDateFilter('endDate', e.target.value)}
                    style={{ width: '140px' }}
                  />
                </div>
              </div>
            </Fieldset>

            {/* Transaction List */}
            <GroupBox label={`Transactions (${filteredTransactions.length})`}>
              <div style={{ padding: '10px' }}>
                {filteredTransactions.length > 0 ? (
                  <div style={{ maxHeight: '400px', overflow: 'auto' }}>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableHeadCell>Date & Time</TableHeadCell>
                          <TableHeadCell>Type</TableHeadCell>
                          <TableHeadCell>Amount</TableHeadCell>
                          <TableHeadCell>Description</TableHeadCell>
                          <TableHeadCell>Reference</TableHeadCell>
                          <TableHeadCell>User</TableHeadCell>
                          <TableHeadCell>Actions</TableHeadCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {filteredTransactions.map((transaction) => (
                          <TableRow key={transaction.id}>
                            <TableDataCell style={{ fontSize: '10px' }}>
                              {new Date(transaction.timestamp).toLocaleDateString()}<br/>
                              <span style={{ color: '#666' }}>
                                {new Date(transaction.timestamp).toLocaleTimeString()}
                              </span>
                            </TableDataCell>
                            <TableDataCell style={{ fontSize: '10px' }}>
                              <span style={{
                                padding: '2px 4px',
                                fontSize: '9px',
                                backgroundColor: getTransactionTypeColor(transaction.type),
                                color: 'white',
                                borderRadius: '2px'
                              }}>
                                {transaction.type.toUpperCase()}
                              </span>
                              {transaction.contactType && (
                                <div style={{ fontSize: '9px', color: '#666', marginTop: '2px' }}>
                                  {transaction.contactType.replace('_', ' ')}
                                </div>
                              )}
                            </TableDataCell>
                            <TableDataCell style={{ 
                              fontSize: '11px', 
                              fontWeight: 'bold',
                              color: getAmountColor(transaction.type, transaction.amount)
                            }}>
                              {transaction.amount ? formatCurrency(transaction.amount) : '-'}
                            </TableDataCell>
                            <TableDataCell style={{ fontSize: '11px', maxWidth: '200px' }}>
                              <div style={{ 
                                overflow: 'hidden', 
                                textOverflow: 'ellipsis', 
                                whiteSpace: 'nowrap' 
                              }}>
                                {transaction.description}
                              </div>
                            </TableDataCell>
                            <TableDataCell style={{ fontSize: '10px', color: '#666' }}>
                              {transaction.reference || '-'}
                            </TableDataCell>
                            <TableDataCell style={{ fontSize: '10px', color: '#666' }}>
                              {transaction.userId}
                            </TableDataCell>
                            <TableDataCell>
                              <Button 
                                size="sm" 
                                onClick={() => handleShowDetails(transaction)}
                                style={{ fontSize: '10px', padding: '2px 6px' }}
                              >
                                Details
                              </Button>
                            </TableDataCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                ) : (
                  <div style={{ 
                    textAlign: 'center', 
                    color: '#666', 
                    fontStyle: 'italic', 
                    padding: '40px' 
                  }}>
                    {clientTransactions.length === 0 
                      ? 'No transactions found for this client'
                      : 'No transactions match the current filters'
                    }
                  </div>
                )}
              </div>
            </GroupBox>
          </div>
        ) : (
          <div style={{ 
            padding: '50px', 
            textAlign: 'center', 
            color: '#666', 
            fontStyle: 'italic' 
          }}>
            <div style={{ fontSize: '48px', marginBottom: '20px' }}>📋</div>
            <div style={{ fontSize: '16px', marginBottom: '10px' }}>No Client Selected</div>
            <div style={{ fontSize: '12px' }}>
              Select a client to view their transaction history
            </div>
          </div>
        )}
      </WindowContent>

      {/* Add Transaction Modal */}
      {showAddModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <Window style={{ width: '500px', maxHeight: '500px' }}>
            <WindowHeader>
              <span>📝 Add New Transaction</span>
            </WindowHeader>
            <WindowContent>
              {renderTransactionForm()}
            </WindowContent>
          </Window>
        </div>
      )}

      {/* Transaction Details Modal */}
      {showDetailModal && selectedTransaction && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <Window style={{ width: '600px', maxHeight: '500px' }}>
            <WindowHeader>
              <span>📋 Transaction Details</span>
            </WindowHeader>
            <WindowContent>
              {renderTransactionDetails()}
            </WindowContent>
          </Window>
        </div>
      )}
    </Window>
  );
};

export default TransactionHistory;